import { Button, Card, Col, notification, Row, Typography } from "antd"
import ClientCard from "components/Client/ClientCard"
import ClientForm, { IClientData } from "components/Client/ClientForm"
import ClientLookup from "components/Client/ClientLookup"
import usePlatform from "contexts/platform/use-platform"
import {
  ICreatePolicyholder,
  IUpdatePolicyholder
} from "platform-client/client/controllers/policyholders"
import { IPolicyholder, PolicyholderType } from "platform-client/types"
import React from "react"
import { useMutation } from "react-query"
import styled from "styled-components"

const StyledButtonWrapper = styled.div`
  position: relative;
  display: flex;
  flex-wrap: nowrap;

  button {
    width: 100%;
  }
`

const buildPayload = (formData: IClientData) => {
  const legalName =
    formData.policyholderType === "Business"
      ? formData.businessName || ""
      : [formData.title, formData.firstName, formData.lastName]
          .filter(Boolean)
          .join(" ")
          .trim()
  return {
    businessName: formData.businessName || null,
    contact: {
      name: {
        firstName: formData.firstName || null,
        lastName: formData.lastName || null,
        title: formData.title || null
      },
      dateOfBirth: formData.dateOfBirth
        ? formData.dateOfBirth.format("YYYY-MM-DD")
        : null,
      phoneNumber: formData.phoneNumber || null,
      emailAddress: formData.emailAddress || null
    },
    contactAddress: {
      line1: formData?.contactAddress?.line1 || null,
      line2: formData?.contactAddress?.line2 || null,
      line3: formData?.contactAddress?.line3 || null,
      locality: formData?.contactAddress?.locality || null,
      province: formData?.contactAddress?.province || null,
      postalCode: formData?.contactAddress?.postalCode || null,
      country: formData?.contactAddress?.country
        ? JSON.parse(formData?.contactAddress?.country)
        : { referenceID: null, text: null },
      provinceReferenceID:
        formData?.contactAddress?.provinceReferenceID ?? null,
      uniqueReference: formData?.contactAddress?.uniqueReference ?? null
    },
    legalName,
    policyholderType: formData.policyholderType as PolicyholderType,
    ownerID: formData.ownerID || null,
    companyID: formData.companyID || null,
    referenceID: formData.referenceID || null
  }
}

interface ISelectClientStepProps {
  client?: IPolicyholder
  onSaveClient: (data: IPolicyholder) => void
}

const SelectClientStep: React.FC<ISelectClientStepProps> = ({
  client: initialClient,
  onSaveClient
}) => {
  const [client, setClient] = React.useState<IPolicyholder | undefined>(
    initialClient
  )
  const [showForm, setShowForm] = React.useState(false)
  const platform = usePlatform()

  React.useEffect(() => {
    setClient(initialClient)
  }, [initialClient])

  const createClient = useMutation((request: ICreatePolicyholder) =>
    platform.policyholders.createPolicyholder(request)
  )

  const updateClient = useMutation(
    (args: { id: string; request: IUpdatePolicyholder }) =>
      platform.policyholders.updatePolicyholder(args.id, args.request)
  )

  const onFinish = async (formData: IClientData) => {
    if (!formData.policyholderType) {
      notification.error({
        message: "Unable to submit form, missing required fields."
      })
      return
    }
    const basePayload = buildPayload(formData)
    const legalName =
      typeof basePayload.legalName === "string" ? basePayload.legalName : ""
    if (!legalName) {
      notification.error({
        message: "Unable to submit form, missing required fields."
      })
      return
    }

    const isEdit = !!client?.id
    const onError = () =>
      notification.error({
        message: isEdit
          ? "Unable to update client, please try again."
          : "Unable to create client, please try again."
      })

    if (isEdit && client) {
      updateClient
        .mutateAsync({
          id: client.id,
          request: {
            ...basePayload,
            legalName,
            id: client.id,
            referenceID: formData.referenceID || client.referenceID || null,
            companyID: client.companyID ?? null
          }
        })
        .then((response) => onSaveClient(response))
        .catch(onError)
    } else {
      createClient
        .mutateAsync({ ...basePayload, legalName })
        .then((response) => onSaveClient(response))
        .catch(onError)
    }
  }

  if (client && !showForm) {
    return (
      <Row>
        <Col span={10} offset={7} style={{ padding: "50px 0 " }}>
          <ClientCard
            client={client}
            onEdit={() => setShowForm(true)}
            onClear={() => setClient(undefined)}
          />
          <StyledButtonWrapper>
            <Button
              size="large"
              type="primary"
              onClick={() => onSaveClient(client)}
            >
              Save and continue
            </Button>
          </StyledButtonWrapper>
        </Col>
      </Row>
    )
  }

  if (!showForm) {
    return (
      <Row>
        <Col
          xs={{ span: 24, offset: 0 }}
          lg={{ span: 10, offset: 7 }}
          style={{ padding: "50px 0 " }}
        >
          <Card style={{ marginBottom: "15px" }}>
            <Card.Meta
              style={{ marginBottom: "15px" }}
              title="Search Clients"
              description="Use the field below to search for clients. You can search by business name, first name, last name, email address, postcode or client ID. Alternatively, you can create a new client."
            />
            <ClientLookup onSelectClient={setClient} />
            <div style={{ marginTop: 12, textAlign: "right" }}>
              <Typography.Link onClick={() => setShowForm(true)}>
                or create a new client
              </Typography.Link>
            </div>
          </Card>
        </Col>
      </Row>
    )
  }

  return (
    <ClientForm
      onCloseForm={() => setShowForm(false)}
      onFinish={onFinish}
      client={client}
      isLoading={createClient.isLoading || updateClient.isLoading}
      isEditMode={!!client?.id}
    />
  )
}

export default SelectClientStep
